import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {CommonUtils} from '@mintdata/mint-kit/impl/util/CommonUtils';
import _mapValues from 'lodash/mapValues';

export type ProviderFn = () => any;

export type TransformerFn = (v:any) => any;

export class PropPathTree
{
    public value:any;
    public subTrees:{[key:string]:PropPathTree};

    constructor(value:any, subTrees:{[key:string]:PropPathTree})
    {
        this.value = value;
        this.subTrees = subTrees;
    }

    public setValue(propPath:PropPath, value:any)
    {
        let tree:PropPathTree = this;
        for( const part of propPath.parts )
        {
            const subTrees = tree.subTrees || (tree.subTrees = {});

            // going down the tree...
            tree = subTrees[part] || (subTrees[part] = new PropPathTree(null, null));
        }

        tree.value = value;
    }

    public getValue(propPath:PropPath):any | null
    {
        let tree:PropPathTree = this;
        for( const part of propPath.parts )
        {
            tree = tree.subTrees && tree.subTrees[part];
            if( !tree )
                return null;
        }

        return tree.value;
    }

    public transform(transformerFn:TransformerFn):PropPathTree
    {
        return new PropPathTree(
            transformerFn(this.value),
            _mapValues(this.subTrees, tree => tree.transform(transformerFn))
        );
    }

    // i.e. upper-level values "shadow" lower-level values
    public transformWithShadowing(transformerFn:TransformerFn):PropPathTree
    {
        if( this.value != null )
        // intentionally skip subTrees here => therefore "shadowing" lower-level values
            return new PropPathTree(transformerFn(this.value), null);
        else
            return new PropPathTree(null, _mapValues(this.subTrees, tree => tree.transformWithShadowing(transformerFn)));
    }

    // transformerFn is used to transform value saved in tree, before using it for overriding
    public overrideClone(target:any, transformerFn:TransformerFn = null):any
    {
        let value = this.value;
        if( value != null && transformerFn )
            value = transformerFn(value);

        if( value != null )
            return value;

        if( target == null )
            return target;

        const subTrees = this.subTrees;
        if( !subTrees )
            return target;

        // targetClone will be created lazily, so we can skip cloning entirely if nothing has changed
        let targetClone = null;
        for( const key in subTrees )
        {
            const subTree = subTrees[key];
            const subTarget = target[key];
            const subValue = subTree.overrideClone(subTarget, transformerFn);
            if( subValue != null )
            {
                if( targetClone == null )
                    targetClone = CommonUtils.shallowClone<any>(target);
                targetClone[key] = subValue;
            }
        }

        return targetClone != null ? targetClone : target;
    }
}
